import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { auth } from '../../services/firebase.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase.js';
import { PageSkeleton } from '../Common/SkeletonLoader.js';
import TrialNotification from './TrialNotification.js';

const AdminLayout = ({ children }) => {
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const user = auth.currentUser;
    const location = useLocation();
    //const cvr = company?.cvr;

    // Add function to get subdomain
    const getSubdomain = () => {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        if (parts.length > 2 && hostname.includes('xn--hndvrkernet-x8aq.dk')) {
            return parts[0];
        }
        return null;
    };

    // Add function to handle logout
    const handleLogout = async () => {
        const hostname = window.location.hostname;
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        const subdomain = getSubdomain();
        
        await auth.signOut();
        
        let url;
        
        if (isLocalhost) {
            url = `${window.location.origin}/admin`;
        } else if (subdomain) {
            url = `${window.location.origin}/admin`;
        } else {
            url = `${window.location.origin}/admin`;
        }
    
        window.location.href = url;
    };

    const handleViewPage = (e) => {
        e.preventDefault();
        const hostname = window.location.hostname;
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        const subdomain = getSubdomain();
        
        let url;
        
        // On localhost, use CVR route
        if (isLocalhost && company?.cvr) {
            url = `${window.location.origin}/${company.cvr}`;
        }
        // On production with subdomain, use root path
        else if (subdomain) {
            url = `${window.location.origin}/`;
        }
        // On production without subdomain, use CVR route
        else if (company?.cvr) {
            url = `${window.location.origin}/${company.cvr}`;
        }
        // Fallback to homepage
        else {
            url = `${window.location.origin}/`;
        }
    
        window.open(url, '_blank');
    };

    useEffect(() => {
        const fetchCompany = async () => {
            if (user?.email) {
                try {
                    const companiesRef = collection(db, 'companies');
                    const q = query(companiesRef, where('companyEmail', '==', user.email));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        const companyData = { 
                            id: querySnapshot.docs[0].id, 
                            ...querySnapshot.docs[0].data() 
                        };
                        setCompany(companyData);
                    }
                } catch (err) {
                    console.error('Error fetching company:', err);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchCompany();
    }, [user]);

    if (loading) return <PageSkeleton />;

    const navigation = [
        { 
            name: 'Dashboard', 
            to: '/admin/dashboard', 
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
            )
        },
        { 
            name: 'Sider', 
            to: '/admin/pages',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2" />
                </svg>
            )
        },
        { 
            name: 'Billedearkiv', 
            to: '/admin/images',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            )
        },
        { 
            name: 'Hjemmeside', 
            to: '/admin/settings',
            icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-gray-100 font-roboto">
            {/* Top navigation */}
            <nav className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Company name row */}
                    <div className="h-16 flex items-center justify-between">
                        <h1 className="text-xl font-semibold font-montserrat text-gray-900">
                            Velkommen {company?.name} <span className="text-xs">({company?.companyEmail})</span>
                        </h1>
                        <div className="flex items-center space-x-4">
                            <Link 
                                onClick={handleViewPage} 
                                to="#"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-gray-900 flex items-center space-x-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>Se hjemmeside</span>
                            </Link>
                            <button
                                onClick={handleLogout}
                                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                            >
                                Log ud
                            </button>
                        </div>
                    </div>
                    {/* Navigation row */}
                    <div className="h-auto md:h-12 py-2 md:py-0">
                        <div className="grid grid-cols-2 md:flex md:space-x-4 gap-2">
                            {navigation.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.to}
                                    className={`flex items-center px-2 py-1.5 text-xs md:text-sm font-medium rounded-md ${
                                        location.pathname === item.to
                                            ? 'bg-brand-light/20 text-brand-dark'  // Active state
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-brand-dark'  // Inactive state
                                    }`}
                                >
                                    <span className="mr-1.5 flex-shrink-0">{item.icon}</span>
                                    <span className="truncate">{item.name}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main content */}
            <main className="flex-1">
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="px-4 py-6 sm:px-0">
                        {children}
                        <TrialNotification company={company} />
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;